import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { blogAPI, brandVoiceAPI, uploadAPI } from "../../lib/api";
import SEOPanel from "../../components/blog/SEOPanel";
import { ArrowLeft, Save, Eye, Sparkles, Image, Loader2, Globe } from "lucide-react";
import { toast } from "sonner";

const PILARES = [
    { value: "musica",          label: "🎤 Música" },
    { value: "fe",              label: "🙏 Fé" },
    { value: "mulheres",        label: "👩 Mulheres" },
    { value: "proposito",       label: "✨ Propósito" },
    { value: "mentorias",       label: "👑 Mentorias" },
    { value: "historia_pessoal",label: "📖 História Pessoal" },
];

const TIPOS_CONTEUDO = [
    { value: "blog",      label: "Post de Blog" },
    { value: "carrossel", label: "Carrossel Instagram" },
    { value: "reels",     label: "Script de Reels" },
    { value: "devocional",label: "Devocional" },
];

const EMPTY = {
    title: "", slug: "", content: "", excerpt: "",
    cover_image: "", pilar: "proposito", tags: [],
    published: false, seo_title: "", seo_description: "",
};

function slugify(str) {
    return str.toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-").replace(/-+/g, "-")
        .slice(0, 80);
}

export default function BlogEditor() {
    const { postId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [seoData, setSeoData] = useState({ seo_title: "", seo_description: "", focus_keyword: "" });
    const [loading, setLoading] = useState(!!postId);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [tab, setTab] = useState("content"); // content | seo
    const [aiTopic, setAiTopic] = useState("");
    const [aiTipo, setAiTipo] = useState("blog");
    const fileRef = useRef();

    useEffect(() => {
        if (postId) {
            blogAPI.list().then(r => {
                const p = (r.data || []).find(p => p.id === postId);
                if (p) {
                    setForm({ ...EMPTY, ...p });
                    setSeoData({ seo_title: p.seo_title || "", seo_description: p.seo_description || "", focus_keyword: "" });
                } else { toast.error("Post não encontrado"); navigate("/blog"); }
            }).catch(() => navigate("/blog")).finally(() => setLoading(false));
        }
    }, [postId]);

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const autoSlug = (title) => {
        set("title", title);
        if (!postId) set("slug", slugify(title));
    };

    const save = async (publish = null) => {
        if (!form.title || !form.slug) { toast.error("Título e slug são obrigatórios"); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                seo_title:       seoData.seo_title || form.title,
                seo_description: seoData.seo_description || form.excerpt,
                published: publish !== null ? publish : form.published,
            };
            if (postId) {
                await blogAPI.update(postId, payload);
                toast.success("Post salvo");
            } else {
                await blogAPI.create(payload);
                toast.success("Post criado");
                navigate("/blog");
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
        finally { setSaving(false); }
    };

    const aiGenerate = async () => {
        if (!aiTopic) { toast.error("Informe o tema"); return; }
        setGenerating(true);
        try {
            const r = await blogAPI.aiGenerate({ pilar: form.pilar, topic: aiTopic, tipo: aiTipo });
            set("content", (form.content ? form.content + "\n\n" : "") + (r.data.content || ""));
            toast.success("Conteúdo gerado com IA");
        } catch { toast.error("Erro na geração com IA. Configure OPENAI_API_KEY."); }
        finally { setGenerating(false); }
    };

    const uploadCover = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await uploadAPI.image(fd);
            set("cover_image", r.data.url);
            toast.success("Imagem enviada");
        } catch { toast.error("Erro ao enviar imagem"); }
        finally { setUploading(false); }
    };

    if (loading) return <div className="p-6 text-stone-400 text-sm">Carregando...</div>;

    return (
        <div className="h-screen flex flex-col bg-stone-950">
            {/* Header */}
            <div className="bg-stone-900 border-b border-stone-800 px-5 py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate("/blog")}
                        className="p-2 rounded-lg text-stone-400 hover:text-stone-100 hover:bg-stone-800 transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <input className="bg-transparent text-stone-100 font-bold text-base focus:outline-none w-72 placeholder:text-stone-600"
                        placeholder="Título do post..." value={form.title} onChange={e => autoSlug(e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex gap-1 bg-stone-800 rounded-lg p-1">
                        {[["content","Conteúdo"],["seo","SEO"]].map(([t,l]) => (
                            <button key={t} onClick={() => setTab(t)}
                                className={`px-3 py-1 text-xs rounded-md transition-all ${tab === t ? "bg-stone-700 text-stone-100" : "text-stone-400 hover:text-stone-200"}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => save(false)} disabled={saving}
                        className="border border-stone-700 text-stone-300 hover:text-stone-100 text-sm px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
                        Rascunho
                    </button>
                    <button onClick={() => save(true)} disabled={saving}
                        className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition-colors">
                        <Globe className="w-3.5 h-3.5" />
                        {saving ? "Salvando..." : "Publicar"}
                    </button>
                </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
                {/* Editor principal */}
                <div className="flex-1 flex flex-col overflow-y-auto p-6 space-y-4">
                    {tab === "content" && (
                        <>
                            {/* Slug e pilar */}
                            <div className="flex gap-3 flex-wrap">
                                <div className="flex-1 min-w-[200px]">
                                    <label className="text-xs text-stone-500 block mb-1">Slug / URL</label>
                                    <div className="flex items-center bg-stone-900 border border-stone-800 rounded-lg px-3 py-2">
                                        <span className="text-xs text-stone-600 mr-1">/</span>
                                        <input className="bg-transparent text-sm text-stone-300 focus:outline-none flex-1 font-mono"
                                            value={form.slug} onChange={e => set("slug", slugify(e.target.value))} />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs text-stone-500 block mb-1">Pilar editorial</label>
                                    <select className="bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-stone-300 focus:outline-none focus:border-amber-600"
                                        value={form.pilar} onChange={e => set("pilar", e.target.value)}>
                                        {PILARES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Capa */}
                            <div>
                                <label className="text-xs text-stone-500 block mb-1">Imagem de capa</label>
                                <div className="flex items-center gap-3">
                                    {form.cover_image && (
                                        <img src={form.cover_image} alt="" className="h-16 w-24 object-cover rounded-lg border border-stone-700" />
                                    )}
                                    <button onClick={() => fileRef.current?.click()} disabled={uploading}
                                        className="flex items-center gap-2 border border-stone-700 text-stone-400 hover:text-stone-200 text-sm px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
                                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                                        {uploading ? "Enviando..." : "Escolher imagem"}
                                    </button>
                                    {form.cover_image && (
                                        <button onClick={() => set("cover_image", "")} className="text-xs text-red-400 hover:text-red-300">Remover</button>
                                    )}
                                    <input type="file" ref={fileRef} className="hidden" accept="image/*" onChange={uploadCover} />
                                </div>
                            </div>

                            {/* Excerpt */}
                            <div>
                                <label className="text-xs text-stone-500 block mb-1">Resumo / Excerpt</label>
                                <textarea rows={2} className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-stone-300 focus:outline-none focus:border-amber-600 resize-none"
                                    placeholder="Resumo curto para listagem e SEO..." value={form.excerpt} onChange={e => set("excerpt", e.target.value)} />
                            </div>

                            {/* AI Generate */}
                            <div className="bg-stone-900 border border-stone-800 rounded-xl p-4 space-y-3">
                                <div className="flex items-center gap-2 text-sm font-medium text-stone-300">
                                    <Sparkles className="w-4 h-4 text-amber-400" /> Gerar conteúdo com IA
                                </div>
                                <div className="flex gap-2">
                                    <input className="flex-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600 placeholder:text-stone-600"
                                        placeholder="ex: propósito de vida mesmo na dor..." value={aiTopic} onChange={e => setAiTopic(e.target.value)}
                                        onKeyDown={e => e.key === "Enter" && aiGenerate()} />
                                    <select className="bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-300 focus:outline-none"
                                        value={aiTipo} onChange={e => setAiTipo(e.target.value)}>
                                        {TIPOS_CONTEUDO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                    <button onClick={aiGenerate} disabled={generating || !aiTopic}
                                        className="flex items-center gap-2 bg-amber-800 hover:bg-amber-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition-colors">
                                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                        {generating ? "Gerando..." : "Gerar"}
                                    </button>
                                </div>
                                <p className="text-xs text-stone-600">O conteúdo será adicionado ao editor usando a Brand Voice configurada.</p>
                            </div>

                            {/* Editor de conteúdo */}
                            <div>
                                <label className="text-xs text-stone-500 block mb-1">Conteúdo (HTML ou Markdown)</label>
                                <textarea className="w-full bg-stone-900 border border-stone-800 rounded-xl px-4 py-3 text-sm text-stone-200 focus:outline-none focus:border-amber-600 resize-none font-mono leading-relaxed"
                                    style={{ minHeight: "400px" }}
                                    placeholder="Escreva o conteúdo do post aqui..."
                                    value={form.content} onChange={e => set("content", e.target.value)} />
                            </div>

                            {/* Tags */}
                            <div>
                                <label className="text-xs text-stone-500 block mb-1">Tags</label>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {form.tags.map((tag, i) => (
                                        <span key={i} className="text-xs px-2 py-0.5 bg-stone-800 text-stone-300 border border-stone-700 rounded-full flex items-center gap-1">
                                            {tag}
                                            <button onClick={() => set("tags", form.tags.filter((_, j) => j !== i))} className="hover:text-red-400">×</button>
                                        </span>
                                    ))}
                                </div>
                                <input className="bg-stone-900 border border-stone-800 rounded-lg px-3 py-1.5 text-xs text-stone-300 focus:outline-none focus:border-amber-600"
                                    placeholder="+ adicionar tag (Enter)"
                                    onKeyDown={e => {
                                        if (e.key === "Enter" && e.target.value.trim()) {
                                            set("tags", [...form.tags, e.target.value.trim()]);
                                            e.target.value = "";
                                        }
                                    }} />
                            </div>
                        </>
                    )}

                    {tab === "seo" && (
                        <SEOPanel
                            data={seoData}
                            onChange={setSeoData}
                            content={form.content}
                            title={form.title}
                        />
                    )}
                </div>

                {/* Sidebar direita — pré-visualização */}
                <div className="w-64 border-l border-stone-800 p-4 space-y-4 overflow-y-auto flex-shrink-0">
                    <div>
                        <p className="text-xs text-stone-500 font-medium mb-2">Publicação</p>
                        <label className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
                            <input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} />
                            Publicado
                        </label>
                    </div>

                    {form.cover_image && (
                        <div>
                            <p className="text-xs text-stone-500 font-medium mb-2">Capa</p>
                            <img src={form.cover_image} alt="" className="w-full rounded-lg object-cover aspect-video" />
                        </div>
                    )}

                    <div>
                        <p className="text-xs text-stone-500 font-medium mb-2">Pilar</p>
                        <p className="text-sm text-stone-300">{PILARES.find(p => p.value === form.pilar)?.label}</p>
                    </div>

                    <div>
                        <p className="text-xs text-stone-500 font-medium mb-2">Caracteres</p>
                        <p className="text-sm text-stone-300">{form.content.length.toLocaleString("pt-BR")}</p>
                        <p className="text-xs text-stone-600 mt-0.5">~{Math.ceil(form.content.split(/\s+/).length / 250)} min de leitura</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
